import { Fragment, useRef } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Calendar, LoaderCircle, X } from 'lucide-react';
import { tv } from 'tailwind-variants';
import { useBoolean, useOnClickOutside } from 'usehooks-ts';

import { formatDate } from '@/utils/format/date';

import { DayPicker, type DisabledDates } from './day-picker';
import { inputStyle } from './input';

export type DatePickerProps = {
  id?: string;
  name?: string;
  value: Date | undefined;
  placeholder?: string;
  disabledDates?: DisabledDates;
  onSelect: (value: Date | undefined) => void;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  loading?: boolean;
  clearable?: boolean;
  hint?: string;
};

export function DatePicker({
  value,
  placeholder,
  disabledDates,
  onSelect,
  className,
  hint,
  disabled = false,
  error = false,
  loading = false,
  clearable = false,
}: DatePickerProps) {
  const dropdownRef = useRef(null);
  const open = useBoolean(false);
  const style = datePickerStyle({ error, loading, disabled, withRight: true });

  useOnClickOutside(dropdownRef, open.setFalse);

  function handleSelect(date: Date | undefined) {
    onSelect(date);
    open.setFalse();
  }

  return (
    <div className="m-px">
      <Menu as="div" className={style.container()}>
        <Menu.Button
          type="button"
          disabled={disabled}
          className={style.input({ className })}
          onClick={open.toggle}
        >
          {value ? formatDate(value) : (placeholder ?? 'Select a date')}
        </Menu.Button>
        <div className={style.inputRight()}>
          <Calendar className={style.inputIcon()} />
        </div>
        <div className={style.inputLoader()}>
          <LoaderCircle className={style.inputLoaderIcon()} />
        </div>

        <Transition
          show={open.value}
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items static className={style.dropdown()} ref={dropdownRef}>
            <div>
              <DayPicker
                value={value}
                onSelect={handleSelect}
                disabled={disabledDates}
              />
              {value && clearable ? (
                <div className={style.actions()}>
                  <div
                    className={style.clearAction()}
                    onClick={() => handleSelect(undefined)}
                  >
                    <X className="size-4" />
                    Clear value
                  </div>
                </div>
              ) : null}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
      {hint ? <div className={style.helperText()}>{hint}</div> : null}
    </div>
  );
}

export const datePickerStyle = tv({
  extend: inputStyle,
  slots: {
    dropdown: `
      absolute
      right-0
      z-10
      mt-2
      origin-top-right
      divide-y
      divide-gray-100
      rounded-md
      bg-white
      shadow-lg
      ring-1
      ring-black/5
      focus:outline-none
    `,
    actions: `
      flex
      w-full
      p-4
      pt-0
    `,
    clearAction: `
      flex
      cursor-pointer
      items-center
      gap-2 text-sm
      text-gray-500
      hover:text-gray-600
    `,
  },
});
